import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useGPUTranscriber } from "./useGPUTranscriber";
import { lang } from "moment";

function useSpeechAPITranscriber(onSpeechComplete, locale) {
  // console.log(onSpeechComplete, "onSpeechCOmpleted")
  // console.log(lang, "lang")
  const [isBusy, setIsBusy] = useState(false);
  const [output, setOutput] = useState(undefined);
  const [isActive, setIsActive] = useState(false);

  const recognitionRef = useRef(null);
  const shouldBeRunningRef = useRef(false);
  const bufferRef = useRef("");          // final-only buffer
  const interimRef = useRef("");         // latest interim
  const lastFinalRef = useRef("");       // last final chunk seen (to skip duplicates)
  const onSpeechCompleteRef = useRef(onSpeechComplete);

  useEffect(() => { onSpeechCompleteRef.current = onSpeechComplete; }, [onSpeechComplete]);

  // --- simple normalizers / de-dupe helpers ---
  const normalize = (s) =>
    s.replace(/\s+/g, " ").replace(/[\u200B-\u200D\uFEFF]/g, "").trim();

  // collapse repeated single words and short phrases (2–5 words)
  const dedupePhrases = (s) =>
    normalize(
      s
        // repeated short phrases: "acute fever" ... "acute fever"
        .replace(/\b(\w+(?:\s+\w+){1,4})\b(?:\s+\1\b)+/gi, "$1")
        // repeated single words: "acute acute"
        .replace(/\b(\w+)(?:\s+\1\b)+/gi, "$1")
    );

  // useEffect(() => {
  //   if (!("webkitSpeechRecognition" in window || "SpeechRecognition" in window)) {
  //     console.warn("Speech Recognition API not supported in this browser.");
  //     return;
  //   }
  //   const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  //   const recog = new SpeechRecognition();

  //   // We keep the session alive and manage when to emit ourselves
  //   recog.continuous = true;
  //   recog.interimResults = true;
  //   recog.maxAlternatives = 1;
  //   recog.lang = lang;

  //   recog.onstart = () => {
  //     setIsBusy(true);
  //     setIsActive(true);
  //   };

  //   recog.onresult = (event) => {
  //     // Process only the new results starting at resultIndex
  //     for (let i = event.resultIndex; i < event.results.length; i++) {
  //       const res = event.results[i];
  //       const transcript = normalize(res[0]?.transcript || "");
  //       if (!transcript) continue;

  //       if (res.isFinal) {
  //         const cleaned = dedupePhrases(transcript);

  //         // Skip if identical to the last final chunk we appended
  //         if (cleaned && cleaned !== lastFinalRef.current) {
  //           // Also avoid creating "... X X" endings
  //           const current = bufferRef.current;
  //           const next = normalize(`${current} ${cleaned}`);
  //           // If buffer already ends with the cleaned chunk, don't append again
  //           if (!current.endsWith(cleaned)) {
  //             bufferRef.current = next;
  //           }
  //           lastFinalRef.current = cleaned;
  //         }
  //         // Optional: you can surface a live combined view if you want
  //         // setOutput({ text: bufferRef.current });
  //       } else {
  //         // Interim: hold separately; do NOT commit to buffer
  //         interimRef.current = transcript;
  //         // If you want a live placeholder while speaking:
  //         // setOutput({ text: normalize(`${bufferRef.current} ${interimRef.current}`), interim: true });
  //       }
  //     }
  //   };

  //   recog.onerror = () => {
  //     setIsBusy(false);
  //     setIsActive(false);
  //     if (shouldBeRunningRef.current) {
  //       try { recog.start(); } catch {}
  //     }
  //   };

  //   recog.onend = () => {
  //     setIsBusy(false);
  //     setIsActive(false);

  //     if (shouldBeRunningRef.current) {
  //       // Ended due to silence—keep session alive while mic is on
  //       try { recog.start(); } catch {}
  //     } else {
  //       // Explicit stop: emit final, cleaned text once
  //       const finalText = dedupePhrases(bufferRef.current);
  //       const finalNormalized = normalize(finalText);
  //       setOutput(finalNormalized ? { text: finalNormalized } : undefined);
  //       if (finalNormalized) onSpeechCompleteRef.current?.(finalNormalized);
  //       bufferRef.current = "";
  //       interimRef.current = "";
  //       lastFinalRef.current = "";
  //     }
  //   };

  //   recognitionRef.current = recog;
  // }, []);

  useEffect(() => {
    if (!("webkitSpeechRecognition" in window || "SpeechRecognition" in window)) {
      console.warn("Speech Recognition API not supported in this browser.");
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recog = new SpeechRecognition();

    recog.continuous = true;
    recog.interimResults = true;
    recog.maxAlternatives = 1;
    recog.lang = locale || "en-US";
    // console.log("SpeechRecognition created with lang:", recog.lang);

    // handlers (same as you have)
    recog.onstart = () => { setIsBusy(true); setIsActive(true); };
    recog.onresult = (event) => {
      // Process only the new results starting at resultIndex
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        const transcript = normalize(res[0]?.transcript || "");
        if (!transcript) continue;

        if (res.isFinal) {
          const cleaned = dedupePhrases(transcript);

          // Skip if identical to the last final chunk we appended
          if (cleaned && cleaned !== lastFinalRef.current) {
            // Also avoid creating "... X X" endings
            const current = bufferRef.current;
            const next = normalize(`${current} ${cleaned}`);
            // If buffer already ends with the cleaned chunk, don't append again
            if (!current.endsWith(cleaned)) {
              bufferRef.current = next;
            }
            lastFinalRef.current = cleaned;
          }
          // Optional: you can surface a live combined view if you want
          // setOutput({ text: bufferRef.current });
        } else {
          // Interim: hold separately; do NOT commit to buffer
          interimRef.current = transcript;
          // If you want a live placeholder while speaking:
          // setOutput({ text: normalize(`${bufferRef.current} ${interimRef.current}`), interim: true });
        }
      }
    };

    recog.onerror = () => {
      setIsBusy(false);
      setIsActive(false);
      if (shouldBeRunningRef.current) {
        try { recog.start(); } catch { }
      }
    };

    recog.onend = () => {
      setIsBusy(false);
      setIsActive(false);

      if (shouldBeRunningRef.current) {
        // Ended due to silence—keep session alive while mic is on
        try { recog.start(); } catch { }
      } else {
        // Explicit stop: emit final, cleaned text once
        const finalText = dedupePhrases(bufferRef.current);
        const finalNormalized = normalize(finalText);
        setOutput(finalNormalized ? { text: finalNormalized } : undefined);
        if (finalNormalized) onSpeechCompleteRef.current?.(finalNormalized);
        bufferRef.current = "";
        interimRef.current = "";
        lastFinalRef.current = "";
      }
    };
    recognitionRef.current = recog;

    // cleanup: stop previous recognition and remove handlers
    return () => {
      try {
        shouldBeRunningRef.current = false;
        if (recognitionRef.current) {
          // remove handlers to avoid calling stale setState after unmount
          recognitionRef.current.onresult = null;
          recognitionRef.current.onstart = null;
          recognitionRef.current.onend = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.stop();
        }
      } catch (e) {
        // ignore
      }
      recognitionRef.current = null;
    };
  }, [locale]); // << include locale here

  const start = useCallback(() => {
    const recog = recognitionRef.current;
    if (!recog || isActive) return;
    bufferRef.current = "";
    interimRef.current = "";
    lastFinalRef.current = "";
    shouldBeRunningRef.current = true;
    try {
      recog.start();
      setOutput(undefined);
    } catch (err) {
      if (err?.name !== "InvalidStateError") console.error("Failed to start recognition:", err);
    }
  }, [isActive]);

  const stop = useCallback(() => {
    const recog = recognitionRef.current;
    shouldBeRunningRef.current = false;
    if (recog) {
      try { recog.stop(); } catch { }
    }
  }, []);

  return useMemo(() => ({
    isBusy,
    output,           // final text only after explicit stop()
    start,
    stop,
    isActive,
    isModelLoading: false,
    progressItems: [],
  }), [isBusy, output, start, stop, isActive]);
}



//  Hybrid Transcriber Hook (GPU + Speech API)
export function useHybridTranscriber(forceMode = null, onSpeechComplete, language) {
  const gpuTranscriber = useGPUTranscriber();
  const speechTranscriber = useSpeechAPITranscriber(onSpeechComplete, language);

  const [mode, setMode] = useState(forceMode ?? "auto");
  const [activeTranscriber, setActiveTranscriber] = useState(speechTranscriber);

  useEffect(() => {
    const detectGPU = async () => {
      if (forceMode === "speech") {
        setMode("speech");
        setActiveTranscriber(speechTranscriber);
        return;
      }

      if (navigator.gpu) {
        try {
          const adapter = await navigator.gpu.requestAdapter();
          const device = await adapter?.requestDevice();
          if (device) {
            setMode("gpu");
            setActiveTranscriber(gpuTranscriber);
            return;
          }
        } catch (err) {
          console.warn(
            "WebGPU init failed, falling back to Speech API:",
            err
          );
        }
      }

      // console.log("WebGPU not available — using Speech API");
      setMode("speech");
      setActiveTranscriber(speechTranscriber);
    };

    detectGPU();
  }, [forceMode, gpuTranscriber, speechTranscriber]);

  return useMemo(
    () => ({ transcriber: activeTranscriber, mode }),
    [activeTranscriber, mode]
  );
}
